import { useEffect, useRef } from 'react';
import { Box, Card, Title } from '@nimbus-ds/components';
import { useTranslation } from 'react-i18next';
import { AiFeature, Domain } from '@tiendanube/common/src/enums';
import ModalAside from 'App/components/lab/ModalAside';
import { CATALOG_PRODUCT_HASHED_SLUG } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import GenerateWithAiLink from 'commons/components/GenerateWithAiLink';
import { useModal } from 'commons/hooks';
import { Brand, Seo, AllTags, ShowTagsAndSeoCard } from './components';
import { onChangeBrandType } from './components/Brand/Brand';
import { onChangeSeoType } from './components/Seo/FormSeo/FormSeo';
import { SeoProps } from './components/Seo/Seo';
import { useTagsAndSeoIA } from './hooks/useTagsAndSeoAI';

export type TagsType = string[];

export type onChangeTagsType = ({
  name,
  value,
}: {
  name: string;
  value: TagsType;
}) => void;

interface TagsAndSeoProps
  extends Omit<
    SeoProps,
    | 'seoTitleAI'
    | 'seoDescriptionAI'
    | 'missingNameAndDescription'
    | 'onClickGenerateWithAI'
  > {
  tags: string[];
  onChangeBrand: onChangeBrandType;
  onChangeSeo: onChangeSeoType;
  onChangeTags: onChangeTagsType;
  brand: string;
  autoOpen?: boolean;
}

function TagsAndSeo({
  tags,
  onChangeBrand,
  brand,
  name,
  description,
  seoDescription,
  seoTitle,
  onChangeSeo,
  onChangeTags,
  seoUrl,
  autoOpen = false,
}: Readonly<TagsAndSeoProps>): JSX.Element {
  const { t } = useTranslation(Domain.CATALOG);
  const [hasCatalogProductHashedSlug] = useHasTags([
    CATALOG_PRODUCT_HASHED_SLUG,
  ]);
  const [isShowModal, openModal, closeModal] = useModal(false);

  const hasAutoOpenedRef = useRef(false);

  const {
    tooltipText,
    hasNameAndDescription,
    handleClickGenerateWithAI,
    setIsGeneratedAI,
    generateAllWithAI,
    isGeneratedAI,
    isGeneratingAI,
  } = useTagsAndSeoIA({
    name,
    description,
    tags,
    brand,
    seoTitle,
    seoDescription,
    onChangeTags,
    onChangeBrand,
    onChangeSeo,
    openModal,
  });

  const handleChangeTags: onChangeTagsType = (tags) => {
    onChangeTags(tags);
    setIsGeneratedAI((prev) => ({ ...prev, tags: false }));
  };

  const handleChangeBrand: onChangeBrandType = (brand) => {
    onChangeBrand(brand);
    setIsGeneratedAI((prev) => ({ ...prev, brand: false }));
  };

  const handleChangeSeo: onChangeSeoType = (seo) => {
    onChangeSeo(seo);
    const { name, language } = seo;
    if (!language) return;
    if (name !== 'seoTitle' && name !== 'seoDescription') return;
    setIsGeneratedAI((prev) => ({
      ...prev,
      [name]: { ...prev[name], [language]: false },
    }));
  };

  const generateWithAiLink = (
    <GenerateWithAiLink
      trackingFeature={AiFeature.AI_PRODUCT_SEO_BRAND_TAGS}
      fontSize="caption"
      onClick={handleClickGenerateWithAI}
      tooltipText={tooltipText}
      disabled={!!tooltipText}
    />
  );

  const renderContent = (
    <Box display="flex" flexDirection="column" gap="4">
      <AllTags
        tags={tags}
        aiGenerated={isGeneratedAI.tags}
        disabled={isGeneratingAI.tags}
        missingNameAndDescription={!hasNameAndDescription}
        onChange={handleChangeTags}
        onClickGenerateWithAI={generateAllWithAI}
      />
      <Brand
        value={brand}
        disabled={isGeneratingAI.brand}
        aiGenerated={isGeneratedAI.brand}
        missingNameAndDescription={!hasNameAndDescription}
        onChange={handleChangeBrand}
        onClickGenerateWithAI={generateAllWithAI}
      />
      <Seo
        name={name}
        description={description}
        seoTitle={seoTitle}
        seoTitleAI={{
          isGeneratingAI: isGeneratingAI.seo,
          isGeneratedAI: isGeneratedAI.seoTitle,
        }}
        seoDescription={seoDescription}
        seoDescriptionAI={{
          isGeneratingAI: isGeneratingAI.seo,
          isGeneratedAI: isGeneratedAI.seoDescription,
        }}
        missingNameAndDescription={!hasNameAndDescription}
        onClickGenerateWithAI={generateAllWithAI}
        onChangeSeo={handleChangeSeo}
        seoUrl={seoUrl}
      />
    </Box>
  );

  useEffect(() => {
    if (!autoOpen || hasAutoOpenedRef.current) return;
    const element = document.getElementById('tags-and-seo-card');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => openModal(), 600);
      hasAutoOpenedRef.current = true;
    }
  }, [autoOpen, openModal]);

  return hasCatalogProductHashedSlug ? (
    <Card>
      <Card.Header>
        <Box display="flex" gap="2" alignItems="center">
          <Title as="h4">{t('products.detail.seo.card.title')}</Title>
          {generateWithAiLink}
        </Box>
      </Card.Header>
      <Card.Body>{renderContent}</Card.Body>
    </Card>
  ) : (
    <>
      <ShowTagsAndSeoCard
        tags={tags}
        onChange={handleChangeTags}
        onOpen={openModal}
        aiGenerated={isGeneratedAI.tags}
        slot={generateWithAiLink}
      />
      <ModalAside
        isOpen={isShowModal}
        onDidDismiss={closeModal}
        headerContent={t('products.detail.tags.title')}
      >
        {renderContent}
      </ModalAside>
    </>
  );
}

export default TagsAndSeo;
